<template>
    <vue-headful :title="'Callback ...'" description="title" />
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import CategoryService from '../../services/category'
import UserService from '../../services/users'
import DiagnosticoService from '../../services/diagnostic'
import AuthService from '../../services/auth'
import AvatarPackService from '../../services/avatarPack'

import parameters from "../../services/parameters";
import initialReaderValidation from "../../mixins/InitialReaderValidation";

import AnalyticEventsMixin from "../../mixins/analyticEvents"
import { AnalyticEventTypes } from '@/constants/AnalyticEventTypes'

export default {
    computed: {
        ...mapState({
            user: (state) => state.userData.user,
            avatar: (state) => state.userData.avatarSelected,
            selectedCategories: (state) => state.userData.selectedCategories,
            survey: (state) => state.userData.survey,
            testDiagnostic: (state) => state.dashboard.testDiagnostic,
            loadingState: (state) => state.loading,
            token: (state) => state.token,
            course: (state) => state.userData.user.course,
        }),
    },
    async created() {
        this.$loading(true)
        try {
            if ((this.$route.query.token && this.$route.query.token !== '') || !this.token) {
                this.resetState()
                this.resetUserState()
                this.resetDashboardState()
                this.setUserLoaded(false)
                const token = this.getUrlParameter('token')
                
                if (!token) {
                    console.error('Token no encontrado en la URL')
                    window.location.href = process.env.VUE_APP_BEEREADERS_LOGIN_URL
                    return
                }
                
                this.setToken(token)

            await AuthService.getInfoByToken()
                .then((response) => {
                    if (response && response.content) {
                        this.setUserData(response.content)
                        if (response.content.userId && response.content.username) {
                            Sentry.setUser({ 
                                id: response.content.userId, 
                                username: response.content.username 
                            });
                        }
                    } else {
                        throw new Error('Respuesta de autenticación inválida')
                    }
                })
                .catch((error) => {
                    console.error('Error en la autenticación:', error)
                    window.location.href = process.env.VUE_APP_BEEREADERS_LOGIN_URL
                })
        } else {
            window.location.href = process.env.VUE_APP_BEEREADERS_LOGIN_URL
        }
        
        // Validar que el usuario esté cargado antes de continuar
        if (!this.user || !this.user.id) {
            console.error('Usuario no válido después de la autenticación')
            window.location.href = process.env.VUE_APP_BEEREADERS_LOGIN_URL
            return
        }

        Promise.all([
            CategoryService.getMetaDataCategoryStudent({ userId: this.user.id }),
            UserService.getUserSummaryInfoByUserId(this.user.id),
            DiagnosticoService.findDiagnosticoPendiente(this.user.id),
            AvatarPackService.getAvatarPackByName('Default-Pack'),
            parameters.getParameterByName(process.env.VUE_APP_PARAM_FOR_INITIAL_READER)
        ])
            .then((responseArray) => {
                try {
                    // Validar que responseArray tenga el número esperado de elementos
                    if (!responseArray || responseArray.length < 5) {
                        throw new Error('Respuesta incompleta de los servicios')
                    }

                    // Categorias - validar respuesta
                    if (responseArray[0] && responseArray[0].content) {
                        this.changeCategories(responseArray[0].content)
                    } else {
                        console.warn('No se pudieron cargar las categorías')
                    }

                    // Usuario - validar respuesta
                    if (responseArray[1] && responseArray[1].content) {
                        this.setUserData(responseArray[1].content)
                    } else {
                        console.warn('No se pudo actualizar la información del usuario')
                    }

                    // Diagnosticos pendientes - validar respuesta
                    if (this.user && this.user.id) {
                        this.setPendingDiagnosticData({
                            testDiagnostic: responseArray[2]?.content || null,
                            userId: this.user.id,
                        })
                    }

                    // Avatar pack por defecto - validar respuesta
                    if (responseArray[3] && responseArray[3].content) {
                        this.setDefaultAvatarPack(responseArray[3].content)
                    } else {
                        console.warn('No se pudo cargar el pack de avatares por defecto')
                    }

                    this.setUserLoaded(true)

                    // Parámetro de inicial reader - validar respuesta
                    const initialReaderParam = responseArray[4]?.content
                    this.setInitialReaderActive(initialReaderParam?.present || false);

                    if (this.validateInitialTour()) {
                        this.initalTour()
                        this.$ga.event('pageLoaded', 'onboarding')
                        this.createAnalyticEvent(AnalyticEventTypes.GoToInitialOnboarding)
                    } else {
                        this.notStartTour()
                    }

                    this.$router.push('/user')
                    
                    this.createAnalyticEvent(AnalyticEventTypes.StudentLoggedIn)
                    this.$ga.event('pageLoaded', 'login')

                    this.setIsMobile(this.isMobileDevice())
                } catch (processingError) {
                    console.error('Error procesando los datos iniciales:', processingError)
                    // En caso de error, intentar continuar con datos por defecto
                    this.setUserLoaded(true)
                    this.$router.push('/user')
                }
            })
            .catch((error) => console.error('Error al obtener la data inicial', error))
            .finally(() => {
                this.$loading(this.loadingState)
            })
        } catch (mainError) {
            console.error('Error crítico en el callback:', mainError)
            window.location.href = process.env.VUE_APP_BEEREADERS_LOGIN_URL
        }
    },
    methods: {
        ...mapActions([
            'setToken',
            'resetState',
            'setLoadingState',
            'changeCategories',
            'setLinguisticVariables',
            'setIsMobile'
        ]),
        ...mapActions('common', ['setLinguisticVariables', 'setInitialReaderActive']),
        ...mapActions('userData', [
            'setUserData',
            'setUserLoaded',
            'setSurvey',
            'resetUserState',
            'setRecievedMessages',
        ]),
        ...mapActions('dashboard', [
            'initalTour',
            'setPendingDiagnosticData',
            'notStartTour',
            'setDefaultAvatarPack',
            'resetDashboardState',
        ]),
        getUrlParameter(sParam = '') {
            try {
                const sPageURL = window.location.search;
                const sURLVariables = new URLSearchParams(sPageURL);
                return sURLVariables.get(sParam)
            } catch (error) {
                console.error('Error obteniendo parámetro de URL:', error)
                return null
            }
        },
        validateInitialTour() {
            try {
                if (!this.avatar || !this.selectedCategories || !this.selectedCategories.length) return true;

                const grade = this.user?.course?.grade || this.grade;
                const isPregrade = ['G0'].some(g => grade === g);
                if (!isPregrade && this.testDiagnostic && this.testDiagnostic.length) return true;
                return false;
            } catch (error) {
                console.error('Error validando tour inicial:', error)
                return true; // En caso de error, mostrar el tour por seguridad
            }
        },
        isMobileDevice(){
            try {
                // Primero verificar el tamaño de la ventana (método más confiable para responsive)
                if (window.innerWidth < 600) {
                    return true;
                }
                
                // Como respaldo, también verificar el user agent
                if (!navigator || !navigator.userAgent) {
                    return false;
                }
                
                return (
                    RegExp(/Android/i).exec(navigator.userAgent) ||
                    RegExp(/webOS/i).exec(navigator.userAgent) ||
                    RegExp(/iPhone/i).exec(navigator.userAgent) ||
                    RegExp(/iPad/i).exec(navigator.userAgent) ||
                    RegExp(/iPad Mini/i).exec(navigator.userAgent)||
                    RegExp(/iPod/i).exec(navigator.userAgent) ||
                    RegExp(/BlackBerry/i).exec(navigator.userAgent) ||
                    RegExp(/Windows Phone/i).exec(navigator.userAgent) ||
                    RegExp(/Redmi/i).exec(navigator.userAgent) ||
                    RegExp(/XiaoMi/i).exec(navigator.userAgent) ||
                    RegExp(/Galaxy/i).exec(navigator.userAgent) ||
                    RegExp(/Nexus/i).exec(navigator.userAgent) ||
                    RegExp(/Pixel/i).exec(navigator.userAgent) ||
                    RegExp(/OnePlus/i).exec(navigator.userAgent) ||
                    RegExp(/Huawei/i).exec(navigator.userAgent) ||
                    RegExp(/HTC/i).exec(navigator.userAgent)
                )
            } catch (error) {
                console.error('Error detectando dispositivo móvil:', error)
                return false; // Valor por defecto en caso de error
            }
        }
    },
    mixins: [initialReaderValidation, AnalyticEventsMixin]
}
</script>
